import classNames from 'classnames'
import { forwardRef, useCallback, useState } from 'react'
import CreatableSelect from 'react-select/creatable'

import type { ReactNode } from 'react'
import type { GroupBase, MenuProps, OptionProps, Props as SelectProps, StylesConfig } from 'react-select'
import type Select from 'react-select/dist/declarations/src/Select'

import { Menu, Option, Control, ValueContainer } from './components'

import InputWrapper from '../InputWrapper'

import './styles.scss'

const styles: StylesConfig = {
  container: (styles) => ({
    ...styles,
    outline: 'none',
    width: '100%',
  }),
  control: (styles) => ({
    ...styles,
    '&:hover': {
      border: 0,
      boxShadow: 'none',
    },
    backgroundColor: '#f9f9f9',
    border: 0,
    borderRadius: 5,
    boxShadow: 'none',
    cursor: 'pointer',
    minHeight: 0,
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    display: 'none',
  }),
  indicatorsContainer: (styles) => ({
    ...styles,
    height: '100%',
    position: 'sticky',
  }),
  input: (styles) => ({
    ...styles,
    color: '#1b1d1f',
    fontSize: '1.4rem',
    fontWeight: 500,
    width: '100%',
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: '#fff',
    border: '0.1rem solid #626262',
    borderRadius: 5,
    maxWidth: 300,
    minWidth: '100%',
    zIndex: 99,
  }),
  menuList: (styles) => ({
    ...styles,
    '&:first-child': {
      borderTopLeftRadius: '0.5rem',
      borderTopRightRadius: '0.5rem',
    },
    '&:last-child': {
      borderBottomLeftRadius: '0.5rem',
      borderBottomRightRadius: '0.5rem',
    },
    padding: 0,
  }),
  menuPortal: (styles) => ({
    ...styles,
    zIndex: 9999,
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: '#ffcdb6',
    color: '#1b1d1f',
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    paddingRight: 6,
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    ':hover': {
      backgroundColor: '#ca3423',
    },
    backgroundColor: '#ff5301',
    color: 'white',
    cursor: 'pointer',
  }),
  option: (styles) => ({
    ...styles,
    alignItems: 'center',
    color: '#626262',
    cursor: 'pointer',
    display: 'flex',
    fontSize: '1.1rem',
    fontWeight: 600,
    padding: '0.8rem 1.6rem',
    width: '100%',
  }),
  placeholder: (styles) => ({
    ...styles,
    color: '#aeaeae',
    fontSize: '1.4rem',
    fontWeight: 500,
    zIndex: 1,
  }),
  singleValue: (styles) => ({
    ...styles,
    marginLeft: '0.4rem',
  }),
  valueContainer: (styles) => ({
    ...styles,
    height: '100%',
    padding: '0 0 0 1.2rem',
  }),
}

export interface Props extends Omit<SelectProps, 'isDisabled'> {
  className?: string
  disabled?: boolean
  error?: ReactNode
  label?: string
  labelPosition?: 'top' | 'left'
  size?: Exclude<SIZES, 'large'>
  tooltip?: ReactNode
}

const CreatableSelectInput = forwardRef<Select<unknown, boolean, GroupBase<unknown>>, Props>(
  (
    {
      autoFocus,
      className,
      disabled,
      label,
      labelPosition = 'top',
      menuIsOpen,
      required,
      size = 'medium',
      tooltip,
      value,
      ...props
    }: Props,
    ref
  ) => {
    const [isMenuOpen, setIsMenuOpen] = useState(menuIsOpen)

    const [isFocused, setIsFocused] = useState(!!autoFocus)

    const classes = classNames('creatable-select-input-wrapper', className, {
      //@ts-expect-error value is an array, not an object
      'has-value': !!value && value.length > 0,
    })

    const handleMenuOpenClose = (isOpen: boolean) => () => setIsMenuOpen(isOpen)

    const MenuCallback = useCallback((menuProps: MenuProps) => Menu(menuProps, 'creatable-select-input-menu'), [])

    const OptionsCallback = useCallback(
      (optionProps: OptionProps) => Option(optionProps, props.components?.Option),
      [props.components?.Option]
    )

    return (
      <InputWrapper
        className={classes}
        disabled={disabled}
        label={label}
        labelPosition={labelPosition}
        required={required}
        size={size}
        tooltip={tooltip}
      >
        <CreatableSelect
          {...props}
          autoFocus={autoFocus}
          classNamePrefix="react-select"
          components={{
            Control,
            DropdownIndicator: null,
            Menu: MenuCallback,
            Option: OptionsCallback,
            ValueContainer,
          }}
          isClearable
          isDisabled={disabled}
          isFocused={isFocused}
          menuIsOpen={isMenuOpen}
          onBlur={() => setIsFocused(false)}
          onFocus={() => setIsFocused(true)}
          onMenuClose={handleMenuOpenClose(false)}
          onMenuOpen={handleMenuOpenClose(true)}
          ref={ref}
          styles={styles}
          value={value}
        />
      </InputWrapper>
    )
  }
)

CreatableSelectInput.displayName = 'CreatableSelectInput'

export default CreatableSelectInput
